/**
 * useTierFilteredNavItems Hook
 * TD-014: Frontend Route Guards - Tier-Based Navigation Filtering
 * FIX-055: Sidebar navigation race condition on initial login
 *
 * Filters navigation items based on the current user's tier level.
 * Items with minTier higher than the user's tier are hidden from the sidebar.
 * Groups with no visible children are also hidden.
 *
 * FIX-055: When the user profile is not yet loaded (tierLevel is 0 or user is null
 * but isAuthenticated is true), show all items unfiltered to avoid the sidebar
 * appearing empty during the auth state transition.
 */
"use client";

import * as React from "react";
import { useAuth } from "@apal/shared-auth";
import type { HumandaNavItemConfig } from "@/config/nav";
import type { NavItemConfig } from "@/types/nav";

export interface UseTierFilteredNavItemsResult {
	/** Filtered navigation items based on user tier level */
	filteredItems: NavItemConfig[];
	/** Whether auth is still loading */
	isLoading: boolean;
	/** Whether the user profile (with tier level) is fully loaded */
	isProfileLoaded: boolean;
}

/**
 * Hook to filter navigation items based on user's tier level.
 *
 * @param items - Array of navigation items with optional minTier requirements
 * @returns Object with filtered items, loading state, and profile loaded state
 *
 * @example
 * ```tsx
 * const { filteredItems, isLoading, isProfileLoaded } = useTierFilteredNavItems(humandaNavItems);
 * return <SideNav items={filteredItems} />;
 * ```
 */
export function useTierFilteredNavItems(
	items: HumandaNavItemConfig[]
): UseTierFilteredNavItemsResult {
	const { isAuthenticated, isLoading, user } = useAuth();

	const isProfileLoaded = Boolean(
		isAuthenticated && user && user.tierLevel >= 1
	);

	const filteredItems = React.useMemo((): NavItemConfig[] => {
		// Still loading auth state - return empty
		if (isLoading) {
			return [];
		}

		// Not authenticated - return empty
		if (!isAuthenticated) {
			return [];
		}

		// FIX-055: Authenticated but profile not yet loaded (user is null or
		// tierLevel is 0). Show all items unfiltered rather than an empty sidebar.
		// This handles the race condition where isAuthenticated flips to true
		// before the user object / tier level is populated.
		if (!user || user.tierLevel < 1) {
			return stripMinTierMetadata(items);
		}

		const userTier = user.tierLevel;

		const userType = user.userType;

		function filterByTierAndRole(navItems: HumandaNavItemConfig[]): NavItemConfig[] {
			return navItems.reduce<NavItemConfig[]>((acc, item) => {
				// Check if user meets tier requirement
				if (item.minTier !== undefined && userTier < item.minTier) {
					return acc;
				}

				// Check if user meets role requirement (when allowedRoles is present)
				if (item.allowedRoles && item.allowedRoles.length > 0) {
					if (!userType || !item.allowedRoles.includes(userType)) {
						return acc;
					}
				}

				// If item has children, filter them recursively
				if (item.items && item.items.length > 0) {
					const filteredChildren = filterByTierAndRole(item.items);

					// If this is a group header (no href) and all children are filtered out, skip it
					if (!item.href && filteredChildren.length === 0) {
						return acc;
					}

					acc.push({
						...item,
						items: filteredChildren.length > 0 ? filteredChildren : undefined,
					});
				} else {
					acc.push(item);
				}

				return acc;
			}, []);
		}

		return filterByTierAndRole(items);
	}, [items, isLoading, isAuthenticated, user]);

	return {
		filteredItems,
		isLoading,
		isProfileLoaded,
	};
}

/**
 * Strip tier metadata and return all items as NavItemConfig[].
 * Used when profile is not loaded yet to show all nav items.
 */
function stripMinTierMetadata(items: HumandaNavItemConfig[]): NavItemConfig[] {
	return items.map((item) => {
		const result: NavItemConfig = { ...item };
		if (item.items && item.items.length > 0) {
			result.items = stripMinTierMetadata(item.items);
		}
		return result;
	});
}
